import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { Badge } from "@/shared/ui/badge";
import { Camera, Filter } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { CategoryWithAssets, Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { getProcessingStatusColor, getProcessingStatusLabel } from "@/features/assets/services/statusColors";

interface AssetListViewProps {
  categories: CategoryWithAssets[];
  onAssetClick: (asset: Asset) => void;
  selectedAssetId?: string;
}

export function AssetListView({ categories, onAssetClick, selectedAssetId }: AssetListViewProps) {
  const [processingFilter, setProcessingFilter] = useState<string | null>(null);

  const allAssets = categories.flatMap(category =>
    category.assets.map(asset => ({
      ...asset,
      categoryName: category.name,
      categoryColor: category.color,
    }))
  );

  const failedAiCount = allAssets.filter(a => a.processing_status === 'FAILED_AI').length;
  const pendingCount  = allAssets.filter(a => a.processing_status === 'PENDING_AI' || a.processing_status === 'PENDING_OCR').length;

  const visibleAssets = processingFilter
    ? allAssets.filter(a => a.processing_status === processingFilter)
    : allAssets;

  return (
    <div className="rounded-md border">
      {(failedAiCount > 0 || pendingCount > 0) && (
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/20 text-xs">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Filter:</span>
          <button
            onClick={() => setProcessingFilter(null)}
            className={cn(
              "rounded-full px-2 py-0.5 border transition-colors",
              processingFilter === null ? "bg-primary text-primary-foreground border-primary" : "bg-transparent border-border hover:bg-muted"
            )}
          >
            All ({allAssets.length})
          </button>
          {failedAiCount > 0 && (
            <button
              onClick={() => setProcessingFilter('FAILED_AI')}
              className={cn(
                "rounded-full px-2 py-0.5 border transition-colors",
                processingFilter === 'FAILED_AI' ? "bg-status-critical text-white border-status-critical" : "bg-status-critical/10 text-status-critical border-status-critical/30 hover:bg-status-critical/20"
              )}
            >
              AI failed ({failedAiCount})
            </button>
          )}
          {pendingCount > 0 && (
            <button
              onClick={() => setProcessingFilter('PENDING_AI')}
              className={cn(
                "rounded-full px-2 py-0.5 border transition-colors",
                processingFilter === 'PENDING_AI' ? "bg-status-info text-white border-status-info" : "bg-status-info/10 text-status-info border-status-info/30 hover:bg-status-info/20"
              )}
            >
              Awaiting ({pendingCount})
            </button>
          )}
        </div>
      )}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]">#</TableHead>
            <TableHead className="w-[80px]">Photo</TableHead>
            <TableHead>Asset Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Manufacturer</TableHead>
            <TableHead>Model Number</TableHead>
            <TableHead>Serial Number</TableHead>
            <TableHead className="text-center">Photos</TableHead>
            <TableHead>Processing</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleAssets.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="h-32 text-center text-muted-foreground">
                {processingFilter ? `No assets match this filter` : 'No assets found'}
              </TableCell>
            </TableRow>
          ) : (
            visibleAssets.map((asset, index) => {
              const firstPhoto = asset.photos[0];
              const isSelected = selectedAssetId === asset.id;

              return (
                <TableRow
                  key={asset.id}
                  className={cn(
                    "cursor-pointer transition-colors",
                    isSelected && "bg-primary/10"
                  )}
                  onClick={() => onAssetClick(asset)}
                >
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell>
                    {firstPhoto?.file_path ? (
                      <img
                        src={firstPhoto.file_path}
                        alt={asset.name}
                        className="w-12 h-12 rounded object-cover border border-border"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded bg-muted flex items-center justify-center border border-border">
                        <Camera className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{asset.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline" style={{ borderColor: asset.categoryColor }}>
                      {asset.categoryName}
                    </Badge>
                  </TableCell>
                  <TableCell>{asset.manufacturer || '-'}</TableCell>
                  <TableCell>{asset.model_number || '-'}</TableCell>
                  <TableCell className="font-mono text-sm">{asset.serial_number || '-'}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">
                      {asset.photos.length}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {asset.processing_status ? (
                      <Badge className={cn("text-xs", getProcessingStatusColor(asset.processing_status))}>
                        {getProcessingStatusLabel(asset.processing_status)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
